import { useEffect, useMemo, useState } from 'react';
import { observer } from 'mobx-react';
import { Drawer, Input, Empty, Spin, Typography, Button, Space, message, Tag } from 'antd';
import { TeamOutlined, RobotOutlined, PlusOutlined, ThunderboltOutlined } from '@ant-design/icons';
import { useStore } from '@/stores';
import type { AgentSearchParams } from '@/api/agents';
import type { AgentDefinition } from '@/api/types';
import { useAgentDirectory } from '@/components/useAgentDirectory';
import { AgentStatusPill, AgentStatusToggle, statusParam, type AgentStatusFilter } from '@/components/agentStatus';
import { colors } from '@/theme/tokens';
import { trackEvent } from '@/lib/telemetry';

const { Text } = Typography;

// Right-anchored drawer that lists every agent in the merchant scope and lets
// the user one-click "Link" each one to the current KB. The inverse of
// features/agents/LinkKnowledgeDrawer.tsx — same UX, opposite direction.
//
// Per project convention (drawers > modals for picker flows): keeps the
// KB detail page visible behind so the operator knows which KB they're
// attaching agents to without losing context.
//
// The SERVER lists the candidates (2 Nov 2026): it searches, filters by audience and status, leaves out the agents
// already linked, and pages. The drawer used to fetch 200 of the practice's 256 agents and filter them here.

/** Candidates per request; "Show more" asks for the next page. */
const LINK_PAGE_SIZE = 50;

interface LinkAgentsDrawerProps {
  open: boolean;
  onClose: () => void;
  /** Set of AgentIds already linked to the KB (will be hidden from picker). */
  alreadyLinkedIds: Set<number>;
  knowledgeTitle: string;
}

export const LinkAgentsDrawer = observer(function LinkAgentsDrawer({
  open,
  onClose,
  alreadyLinkedIds,
  knowledgeTitle,
}: LinkAgentsDrawerProps) {
  const { knowledgeDetailStore: detail, merchantContextStore, themeStore } = useStore();
  const isDark = themeStore.mode === 'dark';

  const [search, setSearch] = useState('');
  const [linkingId, setLinkingId] = useState<number | null>(null);
  const [status, setStatus] = useState<AgentStatusFilter>('all');

  // Audience filter chips — narrows a long agent list down to the relevant slice, on the server.
  const [audienceFilter, setAudienceFilter] = useState<'all' | 'patient' | 'careTeam'>('all');
  const merchantId = merchantContextStore.selectedMerchantId;

  useEffect(() => {
    if (!open) {
      setSearch('');
      setStatus('all');
      setAudienceFilter('all');
    }
  }, [open]);

  const request = useMemo<AgentSearchParams | null>(
    () =>
      !open || merchantId == null
        ? null
        : {
            merchantId,
            search: search.trim() || undefined,
            audience: audienceFilter === 'patient' ? 'patient' : audienceFilter === 'careTeam' ? 'care' : undefined,
            status: statusParam(status),
            excludeIds: alreadyLinkedIds.size > 0 ? [...alreadyLinkedIds] : undefined,
            sort: 'name',
            pageSize: LINK_PAGE_SIZE,
          },
    [open, merchantId, search, audienceFilter, status, alreadyLinkedIds],
  );
  const directory = useAgentDirectory(request);
  const candidates = directory.rows;
  const isLoading = directory.loading && candidates.length === 0;
  const error = merchantId == null && open ? 'No merchant selected.' : directory.error;

  async function handleLink(agent: AgentDefinition) {
    setLinkingId(agent.id);
    try {
      await detail.linkAgent(agent.id);
      message.success(`Linked ${agent.displayName ?? `Agent #${agent.id}`} to "${knowledgeTitle}"`);
      trackEvent('AgentKnowledge.LinkedFromKb', {
        agentId: agent.id,
        knowledgeId: detail.knowledgeId ?? -1,
      });
    } catch (err) {
      message.error(err instanceof Error ? err.message : 'Failed to link agent');
    } finally {
      setLinkingId(null);
    }
  }

  return (
    <Drawer
      open={open}
      onClose={onClose}
      placement="right"
      // 40% of viewport — same width as LinkKnowledgeDrawer for visual
      // consistency. Wide enough to show description + audience chips
      // without truncation, narrow enough to leave the KB detail page visible.
      width="40%"
      destroyOnClose
      title={
        <Space size={10} align="center">
          <TeamOutlined style={{ color: colors.ink[600], fontSize: 18 }} />
          <span>Link agents to this knowledge base</span>
        </Space>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12, height: '100%' }}>
        <Text type="secondary" style={{ fontSize: 13 }}>
          Pick agents that should ground their responses in <b>{knowledgeTitle}</b>. Each agent
          you link here will retrieve from this KB at runtime via the standard knowledge-search
          tool.
        </Text>

        <Input.Search
          placeholder="Search agents by name, slug, or description…"
          allowClear
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />

        <AgentStatusToggle value={status} onChange={setStatus} />

        <Space size={6} wrap>
          <Tag.CheckableTag
            checked={audienceFilter === 'all'}
            onChange={() => setAudienceFilter('all')}
          >
            All
          </Tag.CheckableTag>
          <Tag.CheckableTag
            checked={audienceFilter === 'patient'}
            onChange={() => setAudienceFilter('patient')}
          >
            Patient
          </Tag.CheckableTag>
          <Tag.CheckableTag
            checked={audienceFilter === 'careTeam'}
            onChange={() => setAudienceFilter('careTeam')}
          >
            Care team
          </Tag.CheckableTag>
        </Space>

        {error && (
          <div
            style={{
              padding: 12,
              background: isDark ? `${colors.red[700]}30` : colors.red[100],
              border: `1px solid ${colors.red[500]}`,
              borderRadius: 8,
              fontSize: 12.5,
              color: isDark ? colors.red[100] : colors.red[700],
            }}
          >
            {error}
          </div>
        )}

        <div style={{ flex: 1, overflow: 'auto', display: 'flex', flexDirection: 'column', gap: 8 }}>
          {isLoading ? (
            <div style={{ padding: 32, textAlign: 'center' }}>
              <Spin />
            </div>
          ) : candidates.length === 0 ? (
            <div style={{ padding: 32, textAlign: 'center' }}>
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={
                  search.trim() || audienceFilter !== 'all' || status !== 'all'
                    ? 'No agents match this search / filter.'
                    : alreadyLinkedIds.size > 0
                    ? 'Every agent in this merchant is already linked to this KB.'
                    : 'No agents exist yet. Create one from the Agents page first.'
                }
              />
            </div>
          ) : (
            <>
              {candidates.map((a) => (
                <AgentCard
                  key={a.id}
                  agent={a}
                  isDark={isDark}
                  isLinking={linkingId === a.id}
                  onLink={() => void handleLink(a)}
                />
              ))}
              {directory.more && (
                <div style={{ textAlign: 'center', padding: '4px 0' }}>
                  <Button size="small" loading={directory.loadingMore} onClick={directory.showMore}>
                    Show more ({directory.total! - candidates.length} more)
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </Drawer>
  );
});

// ── Agent row card — name + audience + slug + skill-router + Link button ──

const AgentCard = function AgentCard({
  agent,
  isDark,
  isLinking,
  onLink,
}: {
  agent: AgentDefinition;
  isDark: boolean;
  isLinking: boolean;
  onLink: () => void;
}) {
  // Audience tags — keep in sync with the agent header chip styling so
  // the same agent reads the same way wherever it's surfaced.
  const audienceTags: { label: string; show: boolean }[] = [
    { label: 'Patient', show: !!agent.patientApp },
    { label: 'Care team', show: !!agent.merchantApp },
  ].filter((t) => t.show);

  return (
    <div
      style={{
        padding: '12px 14px',
        border: `1px solid ${isDark ? colors.slate[700] : colors.slate[200]}`,
        background: isDark ? colors.slate[800] : '#FFFFFF',
        borderRadius: 8,
        display: 'flex',
        gap: 12,
        alignItems: 'flex-start',
      }}
    >
      <RobotOutlined
        style={{
          fontSize: 20,
          color: isDark ? colors.slate[300] : colors.slate[500],
          marginTop: 2,
        }}
      />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontSize: 14,
            fontWeight: 600,
            color: isDark ? colors.slate[50] : colors.slate[900],
            marginBottom: 2,
          }}
        >
          {agent.displayName ?? <span style={{ fontStyle: 'italic', opacity: 0.7 }}>(unnamed)</span>}
          <Text type="secondary" style={{ fontSize: 11, fontWeight: 400, marginLeft: 8 }}>
            {agent.canonicalName ?? agent.a2AAgentName ?? `#${agent.id}`}
          </Text>
        </div>
        {agent.description && (
          <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 8 }}>
            {agent.description}
          </Text>
        )}
        <Space size={6} wrap>
          <AgentStatusPill agent={agent} />
          {audienceTags.length === 0 ? (
            <Tag style={{ fontSize: 11 }}>No audience</Tag>
          ) : (
            audienceTags.map((t) => (
              <Tag key={t.label} style={{ fontSize: 11 }}>
                {t.label}
              </Tag>
            ))
          )}
          {agent.enableSkillRouter && (
            <Tag icon={<ThunderboltOutlined />} color="purple" style={{ fontSize: 11 }}>
              Router on
            </Tag>
          )}
        </Space>
      </div>
      <Button
        type="primary"
        ghost
        size="small"
        icon={<PlusOutlined />}
        loading={isLinking}
        onClick={onLink}
      >
        Link
      </Button>
    </div>
  );
};
